import { useAppSettings, useHierarchyContext } from "contexts/AppSettingsContext";
import { SignalRContext } from "contexts/SignalRProvider";
import { useUnsavedChanges } from "contexts/UnsavedChangesContextProvider";
import { UPDATE_TLF_ITEM_INDEX } from "contexts/hierarchy/interface";
import { SET_TOC_REQUEST_STATUS } from "contexts/toc/interface";
import { refreshToc } from "contexts/toc/refreshToc";
import { eHierarchyLevel } from "functions/api/enums";
import type { HierarchyLoaderData } from "functions/api/hierarchy/hierarchyLoader";
import { loadHierarchyTlfs } from "functions/api/hierarchy/loadHierarchyTlfs";
import {
  TlfBundleConflict,
  TlfBundleConflictDiff,
  TlfBundleConflictPageResponse,
  TlfBundleConflictResolution,
  TlfBundleImportFailurePage,
  TlfBundleImportJobStatus,
  TlfBundleImportProgress,
  TlfBundleUploadAnalysis,
} from "functions/api/tlfBundle/interface";
import { tlfBundleApi } from "functions/api/tlfBundle/tlfBundleApi";
import { uploadAndAnalyzeTlfBundle } from "functions/api/tlfBundle/uploadAndAnalyzeTlfBundle";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal, ProgressBar, Spinner } from "react-bootstrap";
import { useLoaderData, useRevalidator } from "react-router-dom";

interface ImportTlfBundleModalProps {
  onClose: () => void;
}

/**
 * Upload and analyse a TLF bundle, then page through its conflict summaries.
 *
 * @param props - Modal callbacks
 * @param props.onClose - Callback used to close the import workflow
 * @returns Upload and conflict review dialog
 */
export const ImportTlfBundleModal = ({
  onClose,
}: ImportTlfBundleModalProps): React.ReactElement => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const unsavedChanges = useUnsavedChanges("tlf-bundle-import-modal");
  const revalidator = useRevalidator();
  const routeLoaderData = useLoaderData() as HierarchyLoaderData;
  const signalR = useContext(SignalRContext);
  const backEndUrl = appSettings.backEndUrl;
  const dispatch = appSettings.dispatch;
  const tocState = appSettings.tocState;
  const hierarchy = hierarchyContext?.hierarchy;
  const hasUnsavedDraft =
    unsavedChanges.hasUnsavedChanges ||
    Boolean(hierarchyContext?.changed) ||
    appSettings.tocState.changed ||
    appSettings.tlfState.changed;
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<"upload" | "review" | "job">("upload");
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [progress, setProgress] = useState<TlfBundleImportProgress | null>(null);
  const [uploadAnalysis, setUploadAnalysis] = useState<TlfBundleUploadAnalysis | null>(null);
  const [defaultResolutionByKind, setDefaultResolutionByKind] = useState<
    Record<string, TlfBundleConflictResolution>
  >({});
  const [overrides, setOverrides] = useState<Record<string, TlfBundleConflictResolution>>({});
  const [conflictPage, setConflictPage] = useState<TlfBundleConflictPageResponse | null>(null);
  const [conflictPageNumber, setConflictPageNumber] = useState<number>(1);
  const [conflictPageLoading, setConflictPageLoading] = useState<boolean>(false);
  const [conflictPageError, setConflictPageError] = useState<string | null>(null);
  const [conflictSearch, setConflictSearch] = useState<string>("");
  const [conflictDiffs, setConflictDiffs] = useState<Record<string, TlfBundleConflictDiff[]>>({});
  const [loadingDiffIds, setLoadingDiffIds] = useState<Set<string>>(new Set());
  const [diffErrors, setDiffErrors] = useState<Record<string, string>>({});
  const [startingImport, setStartingImport] = useState<boolean>(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<TlfBundleImportJobStatus | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);
  const [jobPollVersion, setJobPollVersion] = useState<number>(0);
  const [failurePage, setFailurePage] = useState<TlfBundleImportFailurePage | null>(null);
  const [failureSkip, setFailureSkip] = useState<number>(0);
  const [failureLoading, setFailureLoading] = useState<boolean>(false);
  const [contextRefreshError, setContextRefreshError] = useState<string | null>(null);
  const importJobIsTerminal =
    jobStatus !== null &&
    ["Cancelled", "Completed", "CompletedWithFailures", "Failed"].includes(jobStatus.status);
  const activeUploadController = useRef<AbortController | null>(null);
  const activeConflictController = useRef<AbortController | null>(null);
  const activeDiffControllers = useRef<Map<string, AbortController>>(new Map());
  const activeJobActionController = useRef<AbortController | null>(null);
  const activeContextRefreshController = useRef<AbortController | null>(null);
  const terminalJobIdRef = useRef<string | null>(null);
  const hierarchyBeforeRefreshRef = useRef<typeof hierarchy>(null);
  const mounted = useRef<boolean>(true);

  const loadConflictPage = useCallback(
    async (analysisId: string, pageNumber: number) => {
      /* istanbul ignore else */
      if (hierarchy) {
        activeConflictController.current?.abort();
        const controller = new AbortController();
        activeConflictController.current = controller;
        setConflictPageLoading(true);
        setConflictPageError(null);
        setConflictPageNumber(pageNumber);

        try {
          const response = await tlfBundleApi.listConflicts(
            appSettings.backEndUrl,
            { hierarchyId: hierarchy.id, analysisId, pageNumber, pageSize: 25 },
            controller,
          );
          if (controller.signal.aborted) return;
          if (response.success) {
            setConflictPage(response.data);
            setConflictPageNumber(response.data.pageNumber);
          } else {
            setConflictPageError(response.errorText);
          }
        } catch (error: unknown) {
          if (!controller.signal.aborted) {
            setConflictPageError(
              error instanceof Error ? error.message : "Unable to load conflict summaries",
            );
          }
        } finally {
          if (!controller.signal.aborted) {
            setConflictPageLoading(false);
            // A replacement request aborts this controller before it can replace the reference.
            /* istanbul ignore else */
            if (activeConflictController.current === controller) {
              activeConflictController.current = null;
            }
          }
        }
      }
    },
    [appSettings.backEndUrl, hierarchy],
  );

  /**
   * Load field-level differences for a selected conflict.
   *
   * @param conflict - Conflict whose source and target values should be compared
   */
  const loadConflictDiff = async (conflict: TlfBundleConflict) => {
    if (!hierarchy || !uploadAnalysis || conflictDiffs[conflict.conflictId]) return;

    const controller = new AbortController();
    activeDiffControllers.current.set(conflict.conflictId, controller);
    setLoadingDiffIds((current) => new Set(current).add(conflict.conflictId));
    setDiffErrors((current) => {
      const next = { ...current };
      delete next[conflict.conflictId];
      return next;
    });
    const response = await tlfBundleApi.getConflictDiff(
      appSettings.backEndUrl,
      {
        hierarchyId: hierarchy.id,
        analysisId: uploadAnalysis.analysis.analysisId,
        conflictId: conflict.conflictId,
      },
      controller,
    );
    if (mounted.current && !controller.signal.aborted) {
      if (response.success) {
        setConflictDiffs((current) => ({ ...current, [conflict.conflictId]: response.data }));
      } else {
        setDiffErrors((current) => ({ ...current, [conflict.conflictId]: response.errorText }));
      }
      setLoadingDiffIds((current) => {
        const next = new Set(current);
        next.delete(conflict.conflictId);
        return next;
      });
    }
    if (activeDiffControllers.current.get(conflict.conflictId) === controller) {
      activeDiffControllers.current.delete(conflict.conflictId);
    }
  };

  /**
   * Load one page of failures for the current import job.
   *
   * @param currentJobId - Import job whose failed items should be loaded
   * @param skip - Number of failures to skip
   */
  const loadImportFailures = useCallback(
    async (currentJobId: string, skip: number) => {
      /* istanbul ignore else */
      if (hierarchy) {
        const controller = new AbortController();
        setFailureLoading(true);
        const response = await tlfBundleApi.listImportFailures(
          appSettings.backEndUrl,
          { hierarchyId: hierarchy.id, jobId: currentJobId, skip, take: 25 },
          controller,
        );
        if (mounted.current) {
          if (response.success) {
            setFailurePage(response.data);
            setFailureSkip(response.data.skip);
          } else {
            setJobError(response.errorText);
          }
          setFailureLoading(false);
        }
      }
    },
    [appSettings.backEndUrl, hierarchy],
  );

  /**
   * Submit the analysed upload with the selected conflict resolutions.
   */
  const startImportJob = async () => {
    /* istanbul ignore else */
    if (hierarchy && uploadAnalysis && !startingImport && !hasUnsavedDraft) {
      const controller = new AbortController();
      activeJobActionController.current = controller;
      setStartingImport(true);
      setJobError(null);
      const response = await tlfBundleApi.startImport(
        appSettings.backEndUrl,
        {
          hierarchyId: hierarchy.id,
          uploadId: uploadAnalysis.uploadId,
          analysisId: uploadAnalysis.analysis.analysisId,
          defaultResolutionByKind,
          overrides,
        },
        controller,
      );
      if (mounted.current) {
        if (response.success) {
          setJobId(response.data.jobId);
          setJobStatus(null);
          setFailurePage(null);
          setFailureSkip(0);
          setStage("job");
          setJobPollVersion((version) => version + 1);
        } else {
          setJobError(response.errorText);
        }
        setStartingImport(false);
      }
      // The start button is disabled until this request completes.
      /* istanbul ignore else */
      if (activeJobActionController.current === controller) {
        activeJobActionController.current = null;
      }
    }
  };

  /**
   * Cancel the active import job on the backend.
   */
  const cancelImportJob = async () => {
    /* istanbul ignore else */
    if (hierarchy && jobId) {
      const controller = new AbortController();
      activeJobActionController.current = controller;
      const response = await tlfBundleApi.cancelImport(
        appSettings.backEndUrl,
        { hierarchyId: hierarchy.id, jobId },
        controller,
      );
      if (mounted.current) {
        if (response.success) {
          setJobError(null);
          setJobPollVersion((version) => version + 1);
        } else {
          setJobError(response.errorText);
        }
      }
      if (activeJobActionController.current === controller) {
        activeJobActionController.current = null;
      }
    }
  };

  /**
   * Retry failed items from an eligible import job.
   */
  const retryFailedItems = async () => {
    /* istanbul ignore else */
    if (hierarchy && jobId) {
      const controller = new AbortController();
      activeJobActionController.current = controller;
      const response = await tlfBundleApi.retryFailedImport(
        appSettings.backEndUrl,
        { hierarchyId: hierarchy.id, jobId },
        controller,
      );
      if (mounted.current) {
        if (response.success) {
          terminalJobIdRef.current = null;
          setJobError(null);
          setJobStatus(null);
          setFailurePage(null);
          setJobPollVersion((version) => version + 1);
        } else {
          setJobError(response.errorText);
        }
      }
      if (activeJobActionController.current === controller) {
        activeJobActionController.current = null;
      }
    }
  };

  useEffect(() => {
    mounted.current = true;
    const diffControllers = activeDiffControllers.current;
    return () => {
      mounted.current = false;
      activeUploadController.current?.abort();
      activeConflictController.current?.abort();
      activeJobActionController.current?.abort();
      activeContextRefreshController.current?.abort();
      diffControllers.forEach((controller) => controller.abort());
    };
  }, []);

  useEffect(() => {
    if (jobId && importJobIsTerminal && terminalJobIdRef.current !== jobId) {
      terminalJobIdRef.current = jobId;
      if (hasUnsavedDraft) {
        // The terminal state is the event that surfaces this required save-before-import warning.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setContextRefreshError("Context refresh was skipped because local changes are unsaved");
      } else {
        hierarchyBeforeRefreshRef.current = hierarchy;
        setContextRefreshError(null);
        revalidator.revalidate();
      }
    }
  }, [hasUnsavedDraft, hierarchy, importJobIsTerminal, jobId, revalidator]);

  useEffect(() => {
    const previousHierarchy = hierarchyBeforeRefreshRef.current;
    const refreshedHierarchy = routeLoaderData.hierarchy;
    if (
      previousHierarchy &&
      refreshedHierarchy.id === previousHierarchy.id &&
      refreshedHierarchy !== previousHierarchy &&
      revalidator.state === "idle"
    ) {
      hierarchyBeforeRefreshRef.current = null;
      const controller = new AbortController();
      activeContextRefreshController.current?.abort();
      activeContextRefreshController.current = controller;
      void loadHierarchyTlfs(
        backEndUrl,
        { hierarchyId: refreshedHierarchy.id, parentTag: refreshedHierarchy.parentTag },
        controller,
      ).then((response) => {
        if (mounted.current && !controller.signal.aborted) {
          if (response.success) {
            dispatch({
              operation: UPDATE_TLF_ITEM_INDEX,
              tlfItemIndex: response.data,
              tocMinorVersion: refreshedHierarchy.tocMinorVersion,
            });
          } else {
            setContextRefreshError(response.errorText);
          }
          // A replacement controller aborts this request before its callback runs.
          /* istanbul ignore else */
          if (activeContextRefreshController.current === controller) {
            activeContextRefreshController.current = null;
          }
        }
      });
      if (refreshedHierarchy.level === eHierarchyLevel.Report && !tocState.changed) {
        void refreshToc(
          tocState.tocRequestStatus,
          (tocRequestStatus) => dispatch({ operation: SET_TOC_REQUEST_STATUS, tocRequestStatus }),
          backEndUrl,
          refreshedHierarchy,
          dispatch,
          true,
        );
      }
    }
  }, [
    backEndUrl,
    dispatch,
    tocState.changed,
    tocState.tocRequestStatus,
    revalidator.state,
    routeLoaderData.hierarchy,
  ]);

  useEffect(() => {
    /* istanbul ignore else */
    if (jobId && stage === "job" && hierarchy) {
      let active = true;
      let timeout: number | undefined;
      const controller = new AbortController();
      /**
       * Fetch the latest job state and schedule a fallback poll while the job is active.
       */
      const loadStatus = async () => {
        const response = await tlfBundleApi.getImportStatus(
          appSettings.backEndUrl,
          { hierarchyId: hierarchy.id, jobId },
          controller,
        );
        if (!active || controller.signal.aborted) return;
        if (response.success) {
          const status = response.data;
          setJobStatus(status);
          setJobError(null);
          const isActive =
            status.status === "Queued" ||
            status.status === "Running" ||
            status.status === "CancelRequested";
          if (isActive) {
            timeout = window.setTimeout(() => void loadStatus(), 2000);
          } else if (status.failed > 0) {
            void loadImportFailures(jobId, 0);
          }
        } else {
          setJobError(response.errorText);
          timeout = window.setTimeout(() => void loadStatus(), 5000);
        }
      };
      void loadStatus();
      return () => {
        active = false;
        controller.abort();
        if (timeout !== undefined) window.clearTimeout(timeout);
      };
    }
  }, [appSettings.backEndUrl, hierarchy, jobId, jobPollVersion, stage, loadImportFailures]);

  const signalRConnection = signalR?.connection;
  const connectToGroup = signalR?.connectToGroup;

  useEffect(() => {
    /* istanbul ignore else */
    if (jobId && signalRConnection) {
      /**
       * Process an incoming progress message if it belongs to this import job.
       *
       * @param serializedMessage - Serialized SignalR group message
       */
      const processImportProgress = (serializedMessage: string) => {
        let message: { operation?: string; jsonPayload?: unknown };
        try {
          message = JSON.parse(serializedMessage) as { operation?: string; jsonPayload?: unknown };
        } catch {
          return;
        }
        if (
          message.operation !== "tlfBundleImportProgress" ||
          typeof message.jsonPayload !== "string"
        )
          return;
        try {
          const status = JSON.parse(message.jsonPayload) as TlfBundleImportJobStatus;
          if (status.jobId === jobId) setJobStatus(status);
        } catch {
          setJobError("Unable to read live import progress; checking job status instead.");
        }
      };
      signalRConnection.on("SendMessageToGroup", processImportProgress);
      if (appSettings.prid && connectToGroup) {
        void connectToGroup(`user-${appSettings.prid}`).catch(() => undefined);
      }
      return () => signalRConnection.off("SendMessageToGroup", processImportProgress);
    }
  }, [appSettings.prid, connectToGroup, jobId, signalRConnection]);

  /**
   * Upload the selected file, complete it on the backend, and request analysis.
   */
  const uploadAndAnalyse = async () => {
    /* istanbul ignore else */
    if (file && hierarchy && !uploading) {
      const controller = new AbortController();
      activeUploadController.current = controller;
      setUploading(true);
      setUploadError(null);
      setProgress(null);

      try {
        const response = await uploadAndAnalyzeTlfBundle(
          appSettings.backEndUrl,
          { hierarchyId: hierarchy.id, file },
          controller,
          (nextProgress) => setProgress(nextProgress),
        );
        if (controller.signal.aborted) {
          if (mounted.current) setUploadError("Upload cancelled");
          return;
        }
        if (!response.success) {
          setUploadError(response.errorText);
          return;
        }

        setUploadAnalysis(response.data);
        setStage("review");
        setUploading(false);
        // The upload action stays disabled until this request returns.
        /* istanbul ignore else */
        if (activeUploadController.current === controller) {
          activeUploadController.current = null;
        }
        if (response.data.analysis.conflictCount > 0) {
          void loadConflictPage(response.data.analysis.analysisId, 1);
        }
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          if (mounted.current) setUploadError("Upload cancelled");
        } else {
          setUploadError(error instanceof Error ? error.message : "Unable to upload TLF bundle");
        }
      } finally {
        if (mounted.current) setUploading(false);
        if (activeUploadController.current === controller) activeUploadController.current = null;
      }
    }
  };

  /**
   * Return to file selection and discard the current local review state.
   */
  const chooseAnotherFile = () => {
    activeUploadController.current?.abort();
    activeConflictController.current?.abort();
    activeDiffControllers.current.forEach((controller) => controller.abort());
    activeDiffControllers.current.clear();
    setDefaultResolutionByKind({});
    setOverrides({});
    setConflictDiffs({});
    setLoadingDiffIds(new Set());
    setDiffErrors({});
    setJobError(null);
    setFile(null);
    setProgress(null);
    setUploadAnalysis(null);
    setConflictPage(null);
    setConflictPageNumber(1);
    setConflictPageError(null);
    setConflictPageLoading(false);
    setConflictSearch("");
    setUploadError(null);
    setStage("upload");
  };

  /**
   * Close the dialog and cancel any active upload or conflict-page request.
   */
  const cancelWorkflow = () => {
    activeUploadController.current?.abort();
    activeConflictController.current?.abort();
    onClose();
  };

  const filteredConflicts = (conflictPage?.items ?? []).flatMap((item, index) => {
    let description: string;
    try {
      description = JSON.stringify(item, null, 2) ?? String(item);
    } catch {
      description = "Conflict details could not be displayed";
    }
    return description.toLowerCase().includes(conflictSearch.trim().toLowerCase())
      ? [{ index, description }]
      : [];
  });

  const progressLabel: Record<TlfBundleImportProgress["stage"], string> = {
    compressing: "Compressing bundle",
    uploading: "Uploading bundle",
    completing: "Completing upload",
    analysing: "Analysing bundle",
  };
  return (
    <Modal
      show
      size="xl"
      centered
      onHide={cancelWorkflow}
      aria-labelledby="tlf-bundle-import-title"
    >
      <Modal.Header closeButton>
        <Modal.Title id="tlf-bundle-import-title">Import TLF bundle</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h2 className="h5">
          {stage === "upload"
            ? "Upload bundle"
            : stage === "review"
              ? "Review differences"
              : "Import progress"}
        </h2>
        {stage === "upload" ? (
          <div className="d-grid gap-3">
            <section className="border rounded-2 p-3">
              <h3 className="h6 mb-2">Bundle file</h3>
              <p className="text-muted">Choose a compressed TLF bundle or a JSON file.</p>
              <Form.Group controlId="tlf-bundle-import-file">
                <Form.Label>Select a TLF bundle or JSON file</Form.Label>
                <Form.Control
                  type="file"
                  accept=".tlfbundle.gz,.gz,.json,application/gzip,application/json"
                  disabled={uploading}
                  onChange={(event) => {
                    const input = event.currentTarget;
                    /* istanbul ignore else */
                    // This change handler is attached directly to the file input.
                    if (input instanceof HTMLInputElement) {
                      setFile(input.files?.[0] ?? null);
                    }
                    setUploadError(null);
                  }}
                />
              </Form.Group>
              {file && (
                <p className="mt-2">
                  {file.name} ({file.size.toLocaleString()} bytes)
                </p>
              )}
              {uploadError && (
                <Alert
                  variant="danger"
                  role="alert"
                >
                  {uploadError}
                </Alert>
              )}
            </section>
            {uploading && progress && (
              <section
                className="border rounded-2 p-3"
                aria-live="polite"
              >
                <h3 className="h6 mb-2">Upload progress</h3>
                <p>
                  {progressLabel[progress.stage]}
                  {progress.stage === "uploading" &&
                    `: ${progress.completedChunks} of ${progress.totalChunks} chunks complete`}
                </p>
                <p>
                  {progress.transferredBytes.toLocaleString()} of{" "}
                  {progress.totalBytes.toLocaleString()} bytes
                </p>
                <ProgressBar
                  now={
                    progress.totalBytes === 0
                      ? 0
                      : (progress.transferredBytes / progress.totalBytes) * 100
                  }
                  aria-label="Upload progress"
                />
                <Spinner
                  className="mt-3"
                  animation="border"
                  aria-label="Uploading and analysing bundle"
                />
              </section>
            )}
          </div>
        ) : (
          stage === "review" &&
          uploadAnalysis && (
            <>
              <section className="border rounded-2 p-3 mb-3">
                <h3 className="h6 mb-2">Analysis summary</h3>
                <p>
                  {hierarchy?.name ??
                    // istanbul ignore next -- Upload analysis requires a selected target hierarchy.
                    "The target hierarchy"}{" "}
                  contains {uploadAnalysis.analysis.itemCount.toLocaleString()} uploaded items.
                </p>
                <dl className="d-flex flex-wrap gap-4 mb-0">
                  <div>
                    <dt>New</dt>
                    <dd>{uploadAnalysis.analysis.newItemCount}</dd>
                  </div>
                  <div>
                    <dt>Identical</dt>
                    <dd>{uploadAnalysis.analysis.identicalItemCount}</dd>
                  </div>
                  <div>
                    <dt>Conflicts</dt>
                    <dd>{uploadAnalysis.analysis.conflictCount}</dd>
                  </div>
                  <div>
                    <dt>Item conflicts</dt>
                    <dd>{uploadAnalysis.analysis.itemConflictCount}</dd>
                  </div>
                  <div>
                    <dt>Dependency conflicts</dt>
                    <dd>{uploadAnalysis.analysis.dependencyConflictCount}</dd>
                  </div>
                </dl>
              </section>
              {uploadAnalysis.analysis.reportsToCreate.length > 0 && (
                <section className="border rounded-2 p-3 mb-3">
                  <h3 className="h6 mb-2">Reports to create</h3>
                  <p>These source-study reports are not present in the target hierarchy.</p>
                  <ul className="mb-0">
                    {uploadAnalysis.analysis.reportsToCreate.map((report, index) => (
                      <li key={`${report}-${index}`}>{report}</li>
                    ))}
                  </ul>
                </section>
              )}
              {uploadAnalysis.analysis.conflictCount === 0 ? (
                <section className="border rounded-2 p-3 mb-3">
                  <h3 className="h6 mb-2">Conflict review</h3>
                  <p className="mb-0">No conflicts were found.</p>
                </section>
              ) : (
                <section className="border rounded-2 p-3 mb-3">
                  <h3 className="h6 mb-3">Conflict review</h3>
                  <Form.Group className="mb-3">
                    <Form.Label htmlFor="tlf-bundle-conflict-search">
                      Search current conflict page
                    </Form.Label>
                    <Form.Control
                      id="tlf-bundle-conflict-search"
                      type="search"
                      value={conflictSearch}
                      onChange={(event) => setConflictSearch(event.target.value)}
                    />
                  </Form.Group>
                  {conflictPageLoading && (
                    <Spinner
                      animation="border"
                      aria-label="Loading conflicts"
                    />
                  )}
                  {conflictPageError && (
                    <Alert
                      variant="danger"
                      role="alert"
                    >
                      {conflictPageError}
                      <Button
                        variant="link"
                        onClick={() =>
                          void loadConflictPage(
                            uploadAnalysis.analysis.analysisId,
                            conflictPageNumber,
                          )
                        }
                      >
                        Try again
                      </Button>
                    </Alert>
                  )}
                  {conflictPage && !conflictPageLoading && !conflictPageError && (
                    <>
                      <p aria-live="polite">
                        Page {conflictPage.pageNumber} of {conflictPage.totalPages};{" "}
                        {conflictPage.totalCount} conflicts
                      </p>
                      {[...new Set(conflictPage.items.map((conflict) => conflict.kind))].map(
                        (kind) => {
                          const example = conflictPage.items.find(
                            (conflict) => conflict.kind === kind,
                          );
                          let defaultExample = "";
                          /* istanbul ignore else */
                          // Each kind is derived from these same conflict items.
                          if (example) {
                            defaultExample = ` (${example.defaultResolution})`;
                          }
                          return (
                            <Form.Group
                              className="mb-2"
                              key={kind}
                            >
                              <Form.Label htmlFor={`default-resolution-${kind}`}>
                                Default for {kind}
                              </Form.Label>
                              <Form.Select
                                id={`default-resolution-${kind}`}
                                value={defaultResolutionByKind[kind] ?? ""}
                                onChange={(event) =>
                                  setDefaultResolutionByKind((current) => {
                                    const next = { ...current };
                                    if (event.target.value) {
                                      next[kind] = event.target
                                        .value as TlfBundleConflictResolution;
                                    } else {
                                      delete next[kind];
                                    }
                                    return next;
                                  })
                                }
                              >
                                <option value="">
                                  Use each conflict&apos;s backend default
                                  {defaultExample}
                                </option>
                                {example?.allowedResolutions.map((resolution) => (
                                  <option
                                    key={resolution}
                                    value={resolution}
                                  >
                                    {resolution}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          );
                        },
                      )}
                      {filteredConflicts.length === 0 ? (
                        <p className="mb-0">No conflicts match this search.</p>
                      ) : (
                        <div
                          className="overflow-auto"
                          style={{ maxHeight: "42vh" }}
                        >
                          {filteredConflicts.map(({ index }) => {
                            const conflict = conflictPage.items[index];
                            const diff = conflictDiffs[conflict.conflictId];
                            return (
                              <details
                                key={conflict.conflictId}
                                className="border rounded-2 p-3 mb-2"
                                onToggle={(event) => {
                                  if (event.currentTarget.open && conflict.hasDiff) {
                                    void loadConflictDiff(conflict);
                                  }
                                }}
                              >
                                <summary>
                                  Conflict{" "}
                                  {(conflictPage.pageNumber - 1) * conflictPage.pageSize +
                                    index +
                                    1}
                                  : {conflict.kind}
                                </summary>
                                <p>Conflict ID: {conflict.conflictId}</p>
                                {conflict.source.azsolId !== undefined && (
                                  <p>Source AZSOL ID: {String(conflict.source.azsolId)}</p>
                                )}
                                {conflict.target.azsolId !== undefined && (
                                  <p>Target AZSOL ID: {String(conflict.target.azsolId)}</p>
                                )}
                                {conflict.componentKind && (
                                  <p>Component: {conflict.componentKind}</p>
                                )}
                                {conflict.affectedItems.length > 0 && (
                                  <p>Affected items: {conflict.affectedItems.length}</p>
                                )}
                                <Form.Group className="my-2">
                                  <Form.Label htmlFor={`resolution-${conflict.conflictId}`}>
                                    Resolution
                                  </Form.Label>
                                  <Form.Select
                                    id={`resolution-${conflict.conflictId}`}
                                    value={overrides[conflict.conflictId] ?? ""}
                                    onChange={(event) =>
                                      setOverrides((current) => {
                                        const next = { ...current };
                                        if (event.target.value) {
                                          next[conflict.conflictId] = event.target
                                            .value as TlfBundleConflictResolution;
                                        } else {
                                          delete next[conflict.conflictId];
                                        }
                                        return next;
                                      })
                                    }
                                  >
                                    <option value="">
                                      Use kind default (
                                      {defaultResolutionByKind[conflict.kind] ??
                                        conflict.defaultResolution}
                                      )
                                    </option>
                                    {conflict.allowedResolutions.map((resolution) => (
                                      <option
                                        key={resolution}
                                        value={resolution}
                                      >
                                        {resolution}
                                      </option>
                                    ))}
                                  </Form.Select>
                                </Form.Group>
                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <h3 className="h6">Source</h3>
                                    <pre className="text-wrap">
                                      {JSON.stringify(conflict.source, null, 2)}
                                    </pre>
                                  </div>
                                  <div className="col-md-6">
                                    <h3 className="h6">Target</h3>
                                    <pre className="text-wrap">
                                      {JSON.stringify(conflict.target, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                                {conflict.hasDiff && (
                                  <section aria-label={`Field differences for ${conflict.kind}`}>
                                    <h3 className="h6">Field differences</h3>
                                    {loadingDiffIds.has(conflict.conflictId) && (
                                      <Spinner
                                        animation="border"
                                        aria-label="Loading field differences"
                                      />
                                    )}
                                    {diffErrors[conflict.conflictId] && (
                                      <Alert
                                        variant="danger"
                                        role="alert"
                                      >
                                        {diffErrors[conflict.conflictId]}
                                        <Button
                                          variant="link"
                                          onClick={() => void loadConflictDiff(conflict)}
                                        >
                                          Try again
                                        </Button>
                                      </Alert>
                                    )}
                                    {diff?.map((fieldDiff) => (
                                      <div
                                        key={fieldDiff.path}
                                        className="border-top py-2"
                                      >
                                        <strong>{fieldDiff.path}</strong>
                                        <div className="row g-2">
                                          <div className="col-md-6">
                                            <span>Source: </span>
                                            <code>{JSON.stringify(fieldDiff.sourceValue)}</code>
                                          </div>
                                          <div className="col-md-6">
                                            <span>Target: </span>
                                            <code>{JSON.stringify(fieldDiff.targetValue)}</code>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </section>
                                )}
                              </details>
                            );
                          })}
                        </div>
                      )}
                      <div className="d-flex justify-content-between mt-3">
                        <Button
                          variant="secondary"
                          disabled={conflictPageLoading || conflictPage.pageNumber <= 1}
                          onClick={() =>
                            void loadConflictPage(
                              uploadAnalysis.analysis.analysisId,
                              conflictPage.pageNumber - 1,
                            )
                          }
                        >
                          Previous page
                        </Button>
                        <Button
                          variant="secondary"
                          disabled={
                            conflictPageLoading ||
                            conflictPage.pageNumber >= conflictPage.totalPages
                          }
                          onClick={() =>
                            void loadConflictPage(
                              uploadAnalysis.analysis.analysisId,
                              conflictPage.pageNumber + 1,
                            )
                          }
                        >
                          Next page
                        </Button>
                      </div>
                    </>
                  )}
                </section>
              )}
              {jobError && (
                <Alert
                  variant="danger"
                  role="alert"
                >
                  {jobError}
                </Alert>
              )}
              {hasUnsavedDraft && (
                <Alert
                  variant="warning"
                  role="alert"
                >
                  Save or discard local changes before importing a bundle.
                </Alert>
              )}
            </>
          )
        )}
        {stage === "job" && (
          <>
            {jobError && (
              <Alert
                variant="danger"
                role="alert"
              >
                {jobError}
              </Alert>
            )}
            {contextRefreshError && (
              <Alert
                variant="danger"
                role="alert"
              >
                {contextRefreshError}. Reload the page before editing or saving this hierarchy.
              </Alert>
            )}
            {jobStatus ? (
              <>
                <section className="border rounded-2 p-3 mb-3">
                  <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                    <div>
                      <h3 className="h6 mb-1">Import progress</h3>
                      <p
                        className="text-muted mb-0"
                        aria-live="polite"
                      >
                        {jobStatus.status}
                        {jobStatus.phase ? ` · ${jobStatus.phase}` : ""}
                      </p>
                    </div>
                    <span className="text-muted small">Job {jobStatus.jobId}</span>
                  </div>
                  <dl className="d-flex flex-wrap gap-4 mb-2">
                    <div>
                      <dt>TLF items</dt>
                      <dd>
                        {jobStatus.processedTlfItems.toLocaleString()} of{" "}
                        {jobStatus.totalTlfItems.toLocaleString()} processed
                      </dd>
                    </div>
                    <div>
                      <dt>Hierarchy metadata</dt>
                      <dd>
                        {jobStatus.processedHierarchyMetadata.toLocaleString()} of{" "}
                        {jobStatus.totalHierarchyMetadata.toLocaleString()} processed
                      </dd>
                    </div>
                    <div>
                      <dt>Remaining</dt>
                      <dd>{jobStatus.remaining.toLocaleString()}</dd>
                    </div>
                  </dl>
                  <ProgressBar
                    now={jobStatus.total === 0 ? 0 : (jobStatus.processed / jobStatus.total) * 100}
                    aria-label="Overall import progress"
                  />
                  {jobStatus.error && (
                    <Alert
                      variant="danger"
                      role="alert"
                    >
                      {jobStatus.error}
                    </Alert>
                  )}
                </section>
                {jobStatus.failed > 0 && (
                  <section className="border rounded-2 p-3">
                    <h3 className="h6 mb-2">Failed items</h3>
                    {failureLoading ? (
                      <Spinner
                        animation="border"
                        aria-label="Loading failed items"
                      />
                    ) : !failurePage ? (
                      <Button
                        variant="link"
                        onClick={() =>
                          void loadImportFailures(
                            jobId ??
                              // istanbul ignore next -- Failure controls only render for an active job.
                              "",
                            failureSkip,
                          )
                        }
                      >
                        Try again
                      </Button>
                    ) : (
                      <>
                        {failurePage.items.map((failure) => (
                          <details
                            key={`${failure.sequenceNumber}-${failure.sourceKey}`}
                            className="border-top py-2"
                          >
                            <summary>
                              {failure.kind}: {failure.sourceKey}
                            </summary>
                            <p className="mt-2">{failure.error}</p>
                          </details>
                        ))}
                        <div className="d-flex justify-content-between">
                          <Button
                            variant="secondary"
                            disabled={failureLoading || failureSkip === 0}
                            onClick={() =>
                              void loadImportFailures(
                                jobId ??
                                  // istanbul ignore next -- Failure controls only render for an active job.
                                  "",
                                Math.max(0, failureSkip - 25),
                              )
                            }
                          >
                            Previous failures
                          </Button>
                          <Button
                            variant="secondary"
                            disabled={
                              failureLoading ||
                              failureSkip + failurePage.take >= failurePage.totalCount
                            }
                            onClick={() =>
                              void loadImportFailures(
                                jobId ??
                                  // istanbul ignore next -- Failure controls only render for an active job.
                                  "",
                                failureSkip + failurePage.take,
                              )
                            }
                          >
                            Next failures
                          </Button>
                        </div>
                      </>
                    )}
                  </section>
                )}
              </>
            ) : (
              <section className="border rounded-2 p-3">
                <h3 className="h6 mb-2">Import progress</h3>
                <Spinner
                  animation="border"
                  aria-label="Loading import status"
                />
              </section>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={cancelWorkflow}
        >
          {stage === "job" && importJobIsTerminal ? "Close" : "Cancel"}
        </Button>
        {stage === "review" && (
          <Button
            variant="secondary"
            disabled={startingImport}
            onClick={chooseAnotherFile}
          >
            Choose another file
          </Button>
        )}
        {uploading && (
          <Button
            variant="outline-danger"
            onClick={() => activeUploadController.current?.abort()}
          >
            Cancel upload
          </Button>
        )}
        {stage === "upload" && (
          <Button
            variant="primary"
            disabled={!file || !hierarchy || uploading}
            onClick={() => void uploadAndAnalyse()}
          >
            {uploading ? "Uploading" : "Upload and analyse"}
          </Button>
        )}
        {stage === "review" && uploadAnalysis && (
          <Button
            variant="primary"
            disabled={startingImport || hasUnsavedDraft}
            onClick={() => void startImportJob()}
          >
            {startingImport ? "Starting import" : "Start import"}
          </Button>
        )}
        {stage === "job" &&
          jobStatus &&
          ["Queued", "Running", "CancelRequested"].includes(jobStatus.status) && (
            <Button
              variant="outline-danger"
              onClick={() => void cancelImportJob()}
            >
              Cancel import
            </Button>
          )}
        {stage === "job" &&
          jobStatus &&
          jobStatus.failed > 0 &&
          ["Failed", "CompletedWithFailures"].includes(jobStatus.status) && (
            <Button
              variant="primary"
              onClick={() => void retryFailedItems()}
            >
              Retry failed items
            </Button>
          )}
      </Modal.Footer>
    </Modal>
  );
};

ImportTlfBundleModal.displayName = "ImportTlfBundleModal";
